// ReviewStep Component - Profile wizard review screen
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Chip } from 'primereact/chip';
import { Tag } from 'primereact/tag';

// Helper function to format skill/language values for display
const formatValue = (value) => {
    if (!value) return 'N/A';
    // If it's an object with a name property, use that
    if (typeof value === 'object') {
        return value.skill_name || value.language_name || value.label || 'N/A';
    }
    // If it's a string, format it nicely (convert snake_case to Title Case)
    return String(value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
};

const ReviewStep = ({ data }) => {
    return (
        <div className="grid">
            <div className="col-12">
                <h5 className="mt-0">Review Your Profile</h5>
                <p className="text-600">Please review all the information before saving</p>
            </div>

            {/* Basic Information */}
            <div className="col-12">
                <Card title="Basic Information" className="shadow-1">
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <p className="text-500 text-sm mb-1">Full Name</p>
                            <p className="m-0 font-medium">{data.full_name || 'Not provided'}</p>
                        </div>
                        <div className="col-12 md:col-6">
                            <p className="text-500 text-sm mb-1">Email</p>
                            <p className="m-0 font-medium">{data.email || 'Not provided'}</p>
                        </div>
                        <div className="col-12 md:col-6">
                            <p className="text-500 text-sm mb-1">Phone</p>
                            <p className="m-0 font-medium">{data.phone || 'Not provided'}</p>
                        </div>
                        <div className="col-12 md:col-6">
                            <p className="text-500 text-sm mb-1">Experience Years</p>
                            <p className="m-0 font-medium">{data.experience_years || 0} years</p>
                        </div>
                        {data.headline && (
                            <div className="col-12">
                                <p className="text-500 text-sm mb-1">Headline</p>
                                <p className="m-0 font-medium">{data.headline}</p>
                            </div>
                        )}
                        {data.bio && (
                            <div className="col-12">
                                <p className="text-500 text-sm mb-1">Bio</p>
                                <p className="m-0 line-height-3">{data.bio}</p>
                            </div>
                        )}
                        {(data.current_company || data.current_designation) && (
                            <>
                                <div className="col-12 md:col-6">
                                    <p className="text-500 text-sm mb-1">Current Company</p>
                                    <p className="m-0 font-medium">{data.current_company || 'Not provided'}</p>
                                </div>
                                <div className="col-12 md:col-6">
                                    <p className="text-500 text-sm mb-1">Current Designation</p>
                                    <p className="m-0 font-medium">{data.current_designation || 'Not provided'}</p>
                                </div>
                            </>
                        )}
                        {(data.location_city || data.location_state) && (
                            <div className="col-12">
                                <p className="text-500 text-sm mb-1">Location</p>
                                <p className="m-0 font-medium">
                                    {[data.location_city, data.location_state, data.location_country].filter(Boolean).join(', ')}
                                </p>
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            {/* Work Experience */}
            {data.work_experiences && data.work_experiences.length > 0 && (
                <div className="col-12">
                    <Card title={`Work Experience (${data.work_experiences.length})`} className="shadow-1">
                        {data.work_experiences.map((work, index) => (
                            <div key={index}>
                                {index > 0 && <Divider />}
                                <div className="mb-2">
                                    <h6 className="m-0 mb-1">{work.job_title}</h6>
                                    <p className="text-600 m-0 mb-1">{work.company_name}</p>
                                    <p className="text-sm text-500 m-0">
                                        {work.start_date ? new Date(work.start_date).getFullYear() : 'N/A'} - {work.is_current ? 'Present' : (work.end_date ? new Date(work.end_date).getFullYear() : 'N/A')}
                                        {work.location && ` • ${work.location}`}
                                    </p>
                                    {work.is_current && <Tag value="Current" severity="success" className="mt-2" />}
                                </div>
                            </div>
                        ))}
                    </Card>
                </div>
            )}

            {/* Education */}
            {data.education && data.education.length > 0 && (
                <div className="col-12">
                    <Card title={`Education (${data.education.length})`} className="shadow-1">
                        {data.education.map((edu, index) => (
                            <div key={index}>
                                {index > 0 && <Divider />}
                                <div className="mb-2">
                                    <h6 className="m-0 mb-1">{edu.institution_name}</h6>
                                    <p className="text-600 m-0 mb-1">
                                        {edu.degree_type ? edu.degree_type.replace('_', ' ').toUpperCase() : 'Degree'} in {edu.field_of_study || 'N/A'}
                                    </p>
                                    <p className="text-sm text-500 m-0">
                                        {edu.start_date instanceof Date ? edu.start_date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : (edu.start_date ? new Date(edu.start_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A')} - {edu.is_ongoing ? 'Present' : (edu.end_date instanceof Date ? edu.end_date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : (edu.end_date ? new Date(edu.end_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'))}
                                        {edu.grade && ` • Grade: ${edu.grade}`}
                                    </p>
                                    {edu.is_ongoing && <Tag value="Ongoing" severity="info" className="mt-2" />}
                                </div>
                            </div>
                        ))}
                    </Card>
                </div>
            )}

            {/* Certifications */}
            <div className="col-12">
                <Card title={`Certifications (${data.certifications?.length || 0})`} className="shadow-1">
                    {data.certifications && data.certifications.length > 0 ? (
                        <div className="grid">
                            {data.certifications.map((cert, index) => (
                                <div key={index} className="col-12 md:col-6">
                                    <div className="surface-50 border-round p-3">
                                        <h6 className="m-0 mb-1">{cert.certification_name}</h6>
                                        <p className="text-600 text-sm m-0 mb-1">{cert.issuing_organization}</p>
                                        <p className="text-500 text-xs m-0">
                                            Issued: {cert.issue_date ? new Date(cert.issue_date).toLocaleDateString() : 'N/A'}
                                        </p>
                                        {cert.does_not_expire ? (
                                            <Tag value="No Expiration" severity="success" className="mt-2" />
                                        ) : cert.expiration_date && (
                                            <Tag value={`Expires: ${new Date(cert.expiration_date).toLocaleDateString()}`} severity="warning" className="mt-2" />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-500 m-0">No certifications added</p>
                    )}
                </Card>
            </div>

            {/* Skills & Languages */}
            <div className="col-12 md:col-6">
                <Card title={`Skills (${data.skills?.length || 0})`} className="shadow-1 h-full">
                    {data.skills && data.skills.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {data.skills.map((skill, index) => (
                                <Chip
                                    key={index}
                                    label={formatValue(skill)}
                                    className="bg-primary-50 text-primary-900"
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="text-500 m-0">No skills added</p>
                    )}
                </Card>
            </div>

            <div className="col-12 md:col-6">
                <Card title={`Languages (${data.languages?.length || 0})`} className="shadow-1 h-full">
                    {data.languages && data.languages.length > 0 ? (
                        <div className="flex flex-column gap-2">
                            {data.languages.map((lang, index) => (
                                <div key={index} className="flex justify-content-between align-items-center">
                                    <span className="font-medium">{formatValue(lang)}</span>
                                    <i className="pi pi-check-circle text-green-500"></i>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-500 m-0">No languages added</p>
                    )}
                </Card>
            </div>

            <div className="col-12">
                <div className="surface-100 border-1 surface-border border-round p-3">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-info-circle text-blue-500"></i>
                        <p className="m-0 text-600">
                            Click "Save Profile" to create your agent profile. You can edit it anytime later.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewStep;
